import AmpWrap from '@components/atoms/amp/AmpWrap';
import GridBox from '@components/atoms/GridBox';
import Spacing from '@components/atoms/Spacing';
import COLORS from '@lib/constants/colors';
import PrimaryButton from 'components/atoms/buttons/PrimaryButton';
import { Heading1, Paragraph } from 'components/atoms/Typography';
import BREAKPOINTS from 'lib/constants/breakpoints';
import ROUTES from 'lib/constants/routes';
import { useAmp } from 'next/amp';
import NextImage from 'next/image';
import Link from 'next/link';
import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { Image } from 'types/frontEndTypes';

const Header = styled.header<{ textAlign: 'center' | 'left' }>`
  position: relative;
  padding-top: 80px;
  text-align: ${(props) => props.textAlign};

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding-top: 50px;
    text-align: center;

    a,
    button {
      width: 100%;
    }
  }
`;

const HeaderGrid = styled.div<{ templateColumns: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.templateColumns};
  align-items: center;
  max-width: 1140px;
  margin: 0 auto;

  > div {
    padding: 32px;
  }

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    grid-template-columns: 1fr;

    > div:nth-child(2) {
      display: none;
    }
    > div:first-child {
      max-width: none;
    }
  }
`;

const DefaultHeader: FC<{
  title: ReactNode;
  subtitle: ReactNode;
  buttonText?: string;
  buttonHref?: string;
  image?: Image;
  pastel?: boolean;
  bottomComponent?: ReactNode;
}> = ({
  subtitle,
  title,
  image,
  buttonText,
  pastel = false,
  buttonHref = ROUTES.startForm(),
  bottomComponent,
}) => {
  const amp = useAmp();
  return (
    <Header
      style={{
        backgroundColor: pastel ? COLORS.pastel : '#FFF',
        paddingTop: amp ? 0 : undefined,
      }}
      textAlign={image ? 'left' : 'center'}
    >
      <HeaderGrid templateColumns={image ? '1fr 1fr' : '1fr'}>
        <GridBox
          flexDirection="column"
          justifyContent={image ? 'flex-start' : 'center'}
          style={{ margin: '0 auto', maxWidth: 900 }}
        >
          <Heading1>{title}</Heading1>
          <Spacing top={2} bottom={4}>
            <Paragraph style={{ fontSize: 18 }}>{subtitle}</Paragraph>
          </Spacing>
          {buttonText && (
            <GridBox justifyContent={image ? 'flex-start' : 'center'}>
              <Link passHref href={buttonHref}>
                <a>
                  <PrimaryButton style={{ marginBottom: 16 }} pink boxShadow>
                    {buttonText}
                  </PrimaryButton>
                </a>
              </Link>
            </GridBox>
          )}
        </GridBox>
        {image && (
          <GridBox
            justifyContent="center"
            alignItems="center"
            style={{ position: 'relative', height: 600 }}
          >
            <AmpWrap
              nonAmp={
                image.url ? (
                  <NextImage
                    alt={image.alt}
                    src={image.url}
                    height={image.dimensions?.height}
                    width={image.dimensions?.width}
                  />
                ) : null
              }
            />
          </GridBox>
        )}
      </HeaderGrid>
      {bottomComponent}
    </Header>
  );
};

export default DefaultHeader;
